<template>
  <!-- Custom Schema Editor Modal -->
  <CustomSchemaEditorModal
    v-if="isCustomObjectModalOpen"
    :id-custom-data="currentCustomDataId !== null ? currentCustomDataId : undefined"
    @close="toggleCustomSchemaModal"
  />

  <!-- Action Components -->
  <DeleteSchemaAction
    ref="deleteSchemaRef"
    @confirm="onDeleteConfirm"
  />

  <ApiReferenceModal ref="apiReferenceRef" />

  <div class="objects-data-list-page">
    <!-- Page Header -->
    <PageHeader
      :title="t('myShopData.customObjectsList.title')"
      :summary="t('myShopData.customObjectsList.summary')"
    >
      <template #actions>
        <SpmButton
          :label="t('myShopData.button.addCustomSchema')"
          icon="fa fa-plus"
          class-style="p-button p-button-primary"
          @click="toggleCustomSchemaModal"
        />
      </template>
    </PageHeader>

    <!-- Content Section with DataTable -->
    <div class="page-content">
      <div class="objects-data-list">
        <SpmTableAPI
          v-if="columns.length > 0"
          ref="tableRef"
          mode="client"
          :data="tableData"
          :columns="columns"
          data-key="id_custom_object"
          :show-reload-button="true"
          :client-reload-fn="fetchData"
          :row-actions-options="rowActions"
        >
          <template #empty>
            <SpmEmptyState
              icon="fa-regular fa-database"
              :title="t('myShopData.customObjectsList.emptyState.title')"
              :description="t('myShopData.customObjectsList.emptyState.description')"
              :button-label="t('myShopData.button.addCustomSchema')"
              button-icon="fa fa-plus"
              @action="toggleCustomSchemaModal"
            />
          </template>

          <!-- Slot for schema_name column -->
          <template #cell-schema_name="{ data }">
            <div class="schema-info">
              <div class="schema-name-line">
                <IntegrationOwnerBadge
                  v-if="data.owner_integration"
                  :owner-integration="data.owner_integration"
                />
                <span class="schema-name font-bold">
                  {{ data.schema_name }}
                </span>
              </div>
            </div>
          </template>

          <!-- Slot for status column -->
          <template #cell-status="{ data }">
            <template v-if="typeof data.active === 'boolean'">
              <SpmBadge
                :label="t(`myShopData.status.${data.active ? 'active' : 'inactive'}`)"
                :icon="data.active ? 'fa fa-check-circle' : 'fa fa-times-circle'"
                :colors="data.active
                  ? { bg: 'var(--green-100)', color: 'var(--green-700)' }
                  : { bg: 'var(--red-100)', color: 'var(--red-700)' }"
              />
            </template>
          </template>
        </SpmTableAPI>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import SpmEmptyState from '@/components/table-rest-api/SpmEmptyState.vue';
import type { SpmColumnConfig } from '@/components/table-rest-api/types';
import CustomSchemaEditorModal from '@/components/custom-data/CustomSchemaEditorModal.vue';
import IntegrationOwnerBadge from '@/components/integrations/IntegrationOwnerBadge.vue';
import {
  fetchCustomSchemas,
  SchemaItem,
  activateCustomSchema,
  deactivateCustomSchema,
  deleteCustomSchema,
} from '@/composables/custom-data/customData';
import {
  DeleteSchemaAction,
  ApiReferenceModal,
  useObjectsDataActions,
} from './ObjectsDataListActions';
import type { DeleteSchemaResult } from './ObjectsDataListActions';

export default defineComponent({
  name: 'ObjectsDataList',
  components: {
    SpmButton,
    PageHeader,
    SpmTableAPI,
    SpmBadge,
    SpmEmptyState,
    CustomSchemaEditorModal,
    DeleteSchemaAction,
    ApiReferenceModal,
    IntegrationOwnerBadge,
  },

  setup() {
    const { t } = useI18n();
    const tableRef = ref();

    const tableData = ref<SchemaItem[]>([]);
    const isCustomObjectModalOpen = ref(false);
    const currentCustomDataId = ref<number | null>(null);

    // Action component refs
    const deleteSchemaRef = ref(null);
    const apiReferenceRef = ref(null);

    /**
     * Fetch data from API - used by clientReloadFn prop
     * Updates tableData and returns the data
     */
    const fetchData = async () => {
      tableData.value = await fetchCustomSchemas();
      return tableData.value;
    };

    /**
     * Reload table data using the exposed reloadData method
     */
    const reloadTableData = async () => {
      if (tableRef.value) {
        await tableRef.value.reloadData();
      }
    };

    const setLoading = async (loading: boolean) => {
      if (tableRef.value) {
        await tableRef.value.setLoading(loading);
      }
    };

    const toggleCustomSchemaModal = async () => {
      if (isCustomObjectModalOpen.value) {
        await reloadTableData();
      }
      isCustomObjectModalOpen.value = !isCustomObjectModalOpen.value;
      if (!isCustomObjectModalOpen.value) {
        currentCustomDataId.value = null;
      }
    };

    // Action handlers
    const handleEditSchema = (schemaItem: SchemaItem) => {
      currentCustomDataId.value = schemaItem.id_custom_object ? Number(schemaItem.id_custom_object) : null;
      isCustomObjectModalOpen.value = true;
    };

    const handleActivateSchema = async (schemaItem: SchemaItem) => {
      if (!schemaItem.id_custom_object) return;
      setLoading(true);
      try {
        const success = await activateCustomSchema(schemaItem.id_custom_object);
        if (success) {
          await fetchData();
        }
      } finally {
        setLoading(false);
      }
    };

    const handleDeactivateSchema = async (schemaItem: SchemaItem) => {
      if (!schemaItem.id_custom_object) return;
      setLoading(true);
      try {
        const success = await deactivateCustomSchema(schemaItem.id_custom_object);
        if (success) {
          await fetchData();
        }
      } finally {
        setLoading(false);
      }
    };

    const onDeleteConfirm = async (result: DeleteSchemaResult) => {
      if (!result.schemaItem.id_custom_object) return;
      setLoading(true);
      try {
        const success = await deleteCustomSchema(result.schemaItem.id_custom_object);
        if (success) {
          await fetchData();
        }
      } finally {
        setLoading(false);
      }
    };

    // Use the composable for row actions
    const { rowActions } = useObjectsDataActions({
      deleteSchemaRef,
      apiReferenceRef,
      onEditSchema: handleEditSchema,
      onActivateSchema: handleActivateSchema,
      onDeactivateSchema: handleDeactivateSchema,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'schema_name',
        header: t('myShopData.syncs.schema_name'),
        maxWidth: '40%',
        fields: [
          {
            name: 'schema_name',
            label: t('myShopData.syncs.schema_name'),
            sortable: true,
            filterable: true,
            filterConfig: { type: 'textInput' },
          },
          'last_date_sync',
        ],
      },
      {
        id: 'status',
        header: t('myShopData.syncs.status'),
        fields: [
          {
            name: 'active',
            label: t('myShopData.syncs.status'),
            sortable: true,
            filterable: true,
            filterConfig: {
              type: 'singleSelect',
              options: [
                { label: t('myShopData.status.active'), value: true },
                { label: t('myShopData.status.inactive'), value: false },
              ],
            },
          },
        ],
      },
    ];

    onMounted(() => {
      reloadTableData();
    });

    return {
      t,
      tableRef,
      tableData,
      fetchData,
      columns,
      rowActions,
      isCustomObjectModalOpen,
      currentCustomDataId,
      toggleCustomSchemaModal,
      // Action refs and handlers
      deleteSchemaRef,
      apiReferenceRef,
      onDeleteConfirm,
    };
  },
});
</script>

<style lang="scss" scoped>
/* Page layout */
.objects-data-list-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Page layout */
.objects-data-list-page {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* Schema info styling */
/* Schema info styling */
.schema-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.schema-name {
  font-weight: 500;
  color: var(--text-color);
}

/* Loading state for table */
.objects-data-list {
  position: relative;
  min-height: 200px;
}
</style>
